// =============================================================================
// aura-manager.ts — pure-function port of Services/AuraManager.cs
// No class, no singleton, no module-level state.
//
// Key: Readonly<Record<string, ReadonlyArray<AuraInstance>>> with string keys.
// "-1" key = global aura sentinel (ownerSeat=-1 in Unity).
// "0".."3" = per-seat aura buckets.
//
// getByScope(scope, ownerSeat?):
//   - ownerSeat supplied → return seat's own auras + global ("-1") auras
//   - ownerSeat not supplied → aggregate all seats
// =============================================================================

import type { AuraScope, FacilityCategory, FacilityType } from "@gdg/shared";
import type { CardEffect } from "@gdg/shared";
import { deepFreeze } from "../_freeze.js";
import type { ActionLog } from "../rng.js";
import { assertFiniteInt } from "./_validation.js";

// ---------------------------------------------------------------------------
// AuraInstance (ported from AuraManager.cs AuraInstance class)
// ---------------------------------------------------------------------------

export interface AuraInstance {
  readonly instanceId: number;
  readonly ownerSeat: number;
  readonly sourceCardName: string;
  readonly effect: CardEffect;
  readonly scope: AuraScope;
  readonly isPassive: boolean;
  /** null/undefined = matches all categories */
  readonly categoryFilter?: FacilityCategory | null;
  /**
   * FacilityType-level filter (narrower than categoryFilter).
   * null/undefined = matches all facility types within the category (or all if
   * categoryFilter is also null). When set, the aura only applies to facilities
   * whose FacilityType matches exactly.
   * WEB.4.1: added to support FacilityType-aware AuraApply (e.g. Coal-only, Wind-only).
   */
  readonly facilityFilter?: FacilityType | null;
}

// ---------------------------------------------------------------------------
// AuraManagerState
// ---------------------------------------------------------------------------

export interface AuraManagerState {
  /** keyed by stringified ownerSeat; "-1" = global aura bucket */
  readonly bySeat: Readonly<Record<string, ReadonlyArray<AuraInstance>>>;
}

// ---------------------------------------------------------------------------
// Typed errors
// ---------------------------------------------------------------------------

export class AuraRegisterError extends Error {
  constructor(
    message: string,
    public readonly code: "NullAura" | "Duplicate",
  ) {
    super(message);
    this.name = "AuraRegisterError";
  }
}

// ---------------------------------------------------------------------------
// Result types (event-flag pattern mirrors Group B's handleBalanceChanged)
// ---------------------------------------------------------------------------

export interface RegisterResult {
  readonly newState: AuraManagerState;
  readonly log: ActionLog;
  /** True when aura was added; false when idempotent skip (already registered). */
  readonly added: boolean;
}

export interface DiscardResult {
  readonly newState: AuraManagerState;
  readonly log: ActionLog;
  /** True when aura was found and removed. */
  readonly removed: boolean;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Creates initial AuraManager state with pre-seeded buckets for seats 0-3.
 *
 * R5 #15 (5th relock): NO GLOBAL AURA. The legacy "-1" sentinel bucket has
 * been removed. All auras are scoped per-seat strictly. Cards whose printed
 * text reads "all players" must be implemented as 3 individually-targeted
 * effects, NEVER as a single global bucket.
 */
export function createAuraManagerState(): AuraManagerState {
  const empty: ReadonlyArray<AuraInstance> = Object.freeze([]);
  return Object.freeze({
    bySeat: Object.freeze({
      "0": empty,
      "1": empty,
      "2": empty,
      "3": empty,
    }),
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Register an aura into the per-seat bucket.
 * Idempotent: same (instanceId, scope) pair already registered → skip with warning.
 * Mirrors Unity AuraManager.Register(aura).
 */
export function register(
  state: AuraManagerState,
  log: ActionLog,
  aura: AuraInstance,
): RegisterResult {
  assertFiniteInt(aura.instanceId, "register aura.instanceId");
  assertFiniteInt(aura.ownerSeat, "register aura.ownerSeat");

  const key = String(aura.ownerSeat);
  const existing: ReadonlyArray<AuraInstance> = state.bySeat[key] ?? Object.freeze([]);

  // Dedupe check: same (instanceId, scope, categoryFilter, facilityFilter) tuple
  // already registered — skip silently.
  //
  // WEB.4.UI.X.4 (Net_Zero fix): the dedupe key includes categoryFilter +
  // facilityFilter so multi-scope cards like Net_Zero_Movement (two Toll
  // AuraApply effects with different filters — Fossil mult=0.5 vs Solar mult=2.0)
  // register BOTH auras. Previously the (instanceId, scope) key silently
  // dropped the second authored effect.
  //
  // Two auras with identical (instanceId, scope, cat, ft) — that is, the
  // resolver legitimately registering the same effect twice (re-entry, hook
  // chain) — still get deduped by this guard.
  for (const e of existing) {
    if (
      e.instanceId === aura.instanceId &&
      e.scope === aura.scope &&
      (e.categoryFilter ?? null) === (aura.categoryFilter ?? null) &&
      (e.facilityFilter ?? null) === (aura.facilityFilter ?? null)
    ) {
      console.warn(
        `[AuraManager] Register: instanceId=${aura.instanceId} scope=${aura.scope} cat=${aura.categoryFilter ?? "any"} ft=${aura.facilityFilter ?? "any"} already registered for seat=${aura.ownerSeat} — skipped (idempotent).`,
      );
      return { newState: state, log, added: false };
    }
  }

  const updated: ReadonlyArray<AuraInstance> = Object.freeze([...existing, deepFreeze(aura)]);
  const newBySeat = Object.freeze({ ...state.bySeat, [key]: updated });
  const newState: AuraManagerState = Object.freeze({ bySeat: newBySeat });
  const nextLog = log.append({
    kind: "aura.added",
    data: {
      seat: aura.ownerSeat,
      instanceId: aura.instanceId,
      cardId: aura.sourceCardName,
      scope: aura.scope,
    },
  });
  return { newState, log: nextLog, added: true };
}

/**
 * Remove an aura by instanceId (searches all seats).
 * Returns removed=false and logs warning when instanceId not found.
 * Mirrors Unity AuraManager.Discard(instanceId).
 */
export function discard(
  state: AuraManagerState,
  log: ActionLog,
  instanceId: number,
): DiscardResult {
  assertFiniteInt(instanceId, "discard instanceId");

  for (const key of Object.keys(state.bySeat)) {
    const list: ReadonlyArray<AuraInstance> = state.bySeat[key] ?? Object.freeze([]);
    const idx = list.findIndex((a) => a.instanceId === instanceId);
    if (idx === -1) continue;

    const removed = list[idx];
    if (removed === undefined) continue;

    const updated: ReadonlyArray<AuraInstance> = Object.freeze([
      ...list.slice(0, idx),
      ...list.slice(idx + 1),
    ]);
    const newBySeat = Object.freeze({ ...state.bySeat, [key]: updated });
    const newState: AuraManagerState = Object.freeze({ bySeat: newBySeat });
    const nextLog = log.append({
      kind: "aura.removed",
      data: {
        seat: removed.ownerSeat,
        instanceId: removed.instanceId,
        cardId: removed.sourceCardName,
        scope: removed.scope,
      },
    });
    return { newState, log: nextLog, removed: true };
  }

  console.warn(`[AuraManager] Discard: instance ${instanceId} not found.`);
  return { newState: state, log, removed: false };
}

/**
 * All auras for a given seat (frozen array).
 * Mirrors Unity AuraManager.GetActiveBySeat(seatIndex).
 */
export function getActiveBySeat(
  state: AuraManagerState,
  seatIndex: number,
): ReadonlyArray<AuraInstance> {
  assertFiniteInt(seatIndex, "getActiveBySeat seatIndex");
  return state.bySeat[String(seatIndex)] ?? Object.freeze([]);
}

/**
 * All auras for a given scope, with optional seat filter.
 *
 * - ownerSeat supplied (number) → seat's own auras + global ("-1") auras (same scope).
 *   When ownerSeat === -1, returns only the global bucket (no double-iterate).
 * - ownerSeat not supplied (undefined) → aggregate all seats including global.
 *
 * Mirrors Unity AuraManager.GetByScope(scope, ownerSeat?).
 */
export function getByScope(
  state: AuraManagerState,
  scope: AuraScope,
  ownerSeat?: number,
): ReadonlyArray<AuraInstance> {
  const result: AuraInstance[] = [];

  if (ownerSeat !== undefined) {
    assertFiniteInt(ownerSeat, "getByScope ownerSeat");
    // R5 #15 5th relock: NO GLOBAL AURA. Query the per-seat bucket strictly.
    // The legacy "-1" merge path was removed; cards targeting "all players"
    // must register 3 individual seat-scoped auras at resolve time.
    const key = String(ownerSeat);
    const seatList: ReadonlyArray<AuraInstance> = state.bySeat[key] ?? Object.freeze([]);
    for (const a of seatList) {
      if (a.scope === scope) result.push(a);
    }
    return Object.freeze(result);
  }

  // No seat filter — aggregate all.
  for (const key of Object.keys(state.bySeat)) {
    const list: ReadonlyArray<AuraInstance> = state.bySeat[key] ?? Object.freeze([]);
    for (const a of list) {
      if (a.scope === scope) result.push(a);
    }
  }
  return Object.freeze(result);
}
